import { useEffect, useRef, useCallback, useState } from "react";
import soundtrackUrl from "./assets/soundtrack.mp3";

// Reads the saved high score from local storage when available.
function getInitialHighScore() {
  if (typeof window === "undefined") return 0;
  const saved = localStorage.getItem("asteroids-highscore");
  return saved ? parseInt(saved, 10) : 0;
}

const BASE_WIDTH = 1200;
const BASE_HEIGHT = 800;
const SHIP_SIZE = 20;
const ROTATION_SPEED = 0.08;
const THRUST = 0.15;
const FRICTION = 0.99;
const MAX_BULLETS = 5;
const BULLET_SPEED = 10;
const BULLET_LIFE = 60;
const INVULNERABLE_TIME = 120;

const COLORS = {
  background: "#0a0a0f",
  ship: "#00f6ff",
  asteroid: "#ff2bd6",
  bullet: "#f7ff00",
  text: "#ffe8ff",
  thrust: "#ff7a00",
};

// Generates an irregular polygon used to draw an asteroid shape.
function generateAsteroidVertices(size) {
  const vertices = [];
  const numVertices = 8 + Math.floor(Math.random() * 5);
  for (let i = 0; i < numVertices; i++) {
    const angle = (i / numVertices) * Math.PI * 2;
    const radius = size * (0.7 + Math.random() * 0.3);
    vertices.push({
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    });
  }
  return vertices;
}

// Creates a new asteroid with random movement and a safe spawn distance.
function createAsteroid(x, y, size, existingAsteroids, canvasWidth, canvasHeight) {
  const sizeMap = { large: 50, medium: 30, small: 15 };
  const radius = sizeMap[size];
  const angle = Math.random() * Math.PI * 2;
  const speed = 1 + Math.random() * (size === "large" ? 1 : size === "medium" ? 1.5 : 2);

  let finalX = x;
  let finalY = y;

  if (existingAsteroids.length > 0) {
    let attempts = 0;
    while (attempts < 50) {
      finalX = Math.random() * canvasWidth;
      finalY = Math.random() * canvasHeight;
      const dist = Math.hypot(finalX - canvasWidth / 2, finalY - canvasHeight / 2);
      if (dist > canvasWidth * 0.15) break;
      attempts++;
    }
  }

  return {
    x: finalX,
    y: finalY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.03,
    radius,
    size,
    vertices: generateAsteroidVertices(radius),
  };
}

// Creates a bullet starting from the ship tip with inherited ship momentum.
function createBullet(ship) {
  return {
    x: ship.x + Math.cos(ship.rotation) * ship.radius,
    y: ship.y + Math.sin(ship.rotation) * ship.radius,
    vx: Math.cos(ship.rotation) * BULLET_SPEED + ship.vx * 0.5,
    vy: Math.sin(ship.rotation) * BULLET_SPEED + ship.vy * 0.5,
    rotation: ship.rotation,
    radius: 3,
    life: BULLET_LIFE,
  };
}

// Creates particles used for explosion effects.
function createExplosion(x, y, count) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1 + Math.random() * 4;
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 30 + Math.random() * 20,
      maxLife: 50,
    });
  }
  return particles;
}

// Renders the Astroshoot game, handles looped soundtrack, and runs game logic.
export default function AstroshootGame() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [gameState, setGameState] = useState("start");
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(getInitialHighScore);
  const [lives, setLives] = useState(3);
  const [wave, setWave] = useState(1);
  const [canvasSize, setCanvasSize] = useState({ width: BASE_WIDTH, height: BASE_HEIGHT });
  const [isMobile, setIsMobile] = useState(false);
  const [joystickActive, setJoystickActive] = useState(false);
  const [joystickOffset, setJoystickOffset] = useState({ x: 0, y: 0 });

  const touchRef = useRef({ active: false, startX: 0, startY: 0, currentX: 0, currentY: 0 });
  const fireButtonRef = useRef({ firing: false });

  const gameRef = useRef({
    ship: null,
    asteroids: [],
    bullets: [],
    particles: [],
    keys: {},
    score: 0,
    lives: 3,
    wave: 1,
    frameCount: 0,
    canvasWidth: BASE_WIDTH,
    canvasHeight: BASE_HEIGHT,
  });

  // Starts and manages looped background music during the game session.
  useEffect(() => {
    const soundtrack = new Audio(soundtrackUrl);
    soundtrack.loop = true;
    soundtrack.volume = 0.45;

    // Attempts to start playback and silently handles autoplay restrictions.
    const tryStartPlayback = async () => {
      try {
        await soundtrack.play();
      } catch {
        // Defers audio start until user gesture if blocked.
      }
    };

    tryStartPlayback();

    return () => {
      soundtrack.pause();
      soundtrack.currentTime = 0;
    };
  }, []);

  // Detects if the viewport should use the mobile control layout.
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || "ontouchstart" in window);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Computes responsive canvas dimensions while preserving game aspect ratio.
  useEffect(() => {
    const updateSize = () => {
      const maxWidth = window.innerWidth - 20;
      const maxHeight = window.innerHeight - 20;
      const aspectRatio = BASE_WIDTH / BASE_HEIGHT;

      let width = maxWidth;
      let height = width / aspectRatio;

      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }

      const finalWidth = Math.min(width, BASE_WIDTH);
      const finalHeight = Math.min(height, BASE_HEIGHT);
      setCanvasSize({ width: finalWidth, height: finalHeight });
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Resets all game entities and starts a new run.
  const initGame = useCallback(() => {
    const game = gameRef.current;
    game.canvasWidth = canvasSize.width;
    game.canvasHeight = canvasSize.height;

    game.ship = {
      x: game.canvasWidth / 2,
      y: game.canvasHeight / 2,
      vx: 0,
      vy: 0,
      rotation: -Math.PI / 2,
      radius: SHIP_SIZE,
      thrusting: false,
      invulnerable: false,
      invulnerableTime: 0,
    };
    game.asteroids = [];
    game.bullets = [];
    game.particles = [];
    game.score = 0;
    game.lives = 3;
    game.wave = 1;

    for (let i = 0; i < 4; i++) {
      game.asteroids.push(createAsteroid(0, 0, "large", game.asteroids, game.canvasWidth, game.canvasHeight));
    }

    setScore(0);
    setLives(3);
    setWave(1);
    setGameState("playing");
  }, [canvasSize]);

  // Spawns a new asteroid wave after the current one is cleared.
  const spawnWave = useCallback(() => {
    const game = gameRef.current;
    const count = Math.min(4 + game.wave - 1, 10);
    for (let i = 0; i < count; i++) {
      game.asteroids.push(createAsteroid(0, 0, "large", game.asteroids, game.canvasWidth, game.canvasHeight));
    }
    setWave(game.wave);
  }, []);

  // Wraps object positions around the screen boundaries.
  const wrapPosition = (obj, width, height) => {
    if (obj.x < -obj.radius) obj.x = width + obj.radius;
    if (obj.x > width + obj.radius) obj.x = -obj.radius;
    if (obj.y < -obj.radius) obj.y = height + obj.radius;
    if (obj.y > height + obj.radius) obj.y = -obj.radius;
  };

  // Checks circular collision between two game objects.
  const checkCollision = (a, b) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return dist < a.radius + b.radius;
  };

  // Checks whether an object has completely moved outside the visible canvas.
  const isOutOfBounds = (obj, width, height) => {
    return (
      obj.x < -obj.radius ||
      obj.x > width + obj.radius ||
      obj.y < -obj.radius ||
      obj.y > height + obj.radius
    );
  };

  // Captures keyboard input for movement, firing, and start/restart actions.
  useEffect(() => {
    const handleKeyDown = (e) => {
      const game = gameRef.current;
      game.keys[e.code] = true;

      if (e.code === "Space") {
        e.preventDefault();
        if (gameState === "start" || gameState === "gameover") {
          initGame();
        }
      }
    };

    const handleKeyUp = (e) => {
      gameRef.current.keys[e.code] = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [gameState, initGame]);

  // Runs the main update-render loop while the game is in play state.
  useEffect(() => {
    if (gameState !== "playing") return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const game = gameRef.current;
    game.canvasWidth = canvasSize.width;
    game.canvasHeight = canvasSize.height;

    let animationId;
    let lastFireTime = 0;

    const gameLoop = () => {
      game.frameCount++;

      const touch = touchRef.current;
      const isFiring = fireButtonRef.current.firing;

      if (game.ship) {
        let rotateLeft = game.keys["ArrowLeft"] || game.keys["KeyA"];
        let rotateRight = game.keys["ArrowRight"] || game.keys["KeyD"];
        let thrust = game.keys["ArrowUp"] || game.keys["KeyW"];

        if (touch.active) {
          const dx = touch.currentX - touch.startX;
          const dy = touch.currentY - touch.startY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const threshold = 10;

          if (distance > threshold) {
            const angle = Math.atan2(dy, dx);
            const currentRotation = game.ship.rotation;
            const targetAngle = angle + Math.PI / 2;

            let angleDiff = targetAngle - currentRotation;
            while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
            while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

            if (angleDiff > 0.2) rotateRight = true;
            else if (angleDiff < -0.2) rotateLeft = true;

            thrust = true;
          }
        }

        if (rotateLeft) game.ship.rotation -= ROTATION_SPEED;
        if (rotateRight) game.ship.rotation += ROTATION_SPEED;

        if (thrust) {
          game.ship.vx += Math.cos(game.ship.rotation) * THRUST;
          game.ship.vy += Math.sin(game.ship.rotation) * THRUST;
          game.ship.thrusting = true;
        } else {
          game.ship.thrusting = false;
        }

        if ((game.keys["Space"] || isFiring) && game.frameCount - lastFireTime > 10) {
          if (game.bullets.length < MAX_BULLETS) {
            game.bullets.push(createBullet(game.ship));
            lastFireTime = game.frameCount;
          }
        }

        game.ship.vx *= FRICTION;
        game.ship.vy *= FRICTION;
        game.ship.x += game.ship.vx;
        game.ship.y += game.ship.vy;
        wrapPosition(game.ship, game.canvasWidth, game.canvasHeight);

        if (game.ship.invulnerable) {
          game.ship.invulnerableTime--;
          if (game.ship.invulnerableTime <= 0) {
            game.ship.invulnerable = false;
          }
        }
      }

      for (const asteroid of game.asteroids) {
        asteroid.x += asteroid.vx;
        asteroid.y += asteroid.vy;
        asteroid.rotation += asteroid.rotationSpeed || 0;
        wrapPosition(asteroid, game.canvasWidth, game.canvasHeight);
      }

      for (let i = game.bullets.length - 1; i >= 0; i--) {
        const bullet = game.bullets[i];
        bullet.x += bullet.vx;
        bullet.y += bullet.vy;
        bullet.life--;
        if (bullet.life <= 0 || isOutOfBounds(bullet, game.canvasWidth, game.canvasHeight)) {
          game.bullets.splice(i, 1);
        }
      }

      for (let i = game.particles.length - 1; i >= 0; i--) {
        const particle = game.particles[i];
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vx *= 0.98;
        particle.vy *= 0.98;
        particle.life--;
        if (particle.life <= 0) {
          game.particles.splice(i, 1);
        }
      }

      for (let i = game.bullets.length - 1; i >= 0; i--) {
        const bullet = game.bullets[i];
        for (let j = game.asteroids.length - 1; j >= 0; j--) {
          const asteroid = game.asteroids[j];
          if (checkCollision(bullet, asteroid)) {
            game.bullets.splice(i, 1);
            game.particles.push(...createExplosion(asteroid.x, asteroid.y, 15));

            const points = asteroid.size === "large" ? 20 : asteroid.size === "medium" ? 50 : 100;
            game.score += points;

            if (asteroid.size === "large") {
              game.asteroids.push(
                createAsteroid(asteroid.x, asteroid.y, "medium", game.asteroids, game.canvasWidth, game.canvasHeight),
                createAsteroid(asteroid.x, asteroid.y, "medium", game.asteroids, game.canvasWidth, game.canvasHeight)
              );
            } else if (asteroid.size === "medium") {
              game.asteroids.push(
                createAsteroid(asteroid.x, asteroid.y, "small", game.asteroids, game.canvasWidth, game.canvasHeight),
                createAsteroid(asteroid.x, asteroid.y, "small", game.asteroids, game.canvasWidth, game.canvasHeight)
              );
            }

            game.asteroids.splice(j, 1);
            setScore(game.score);

            if (game.score > 0 && game.score % 10000 === 0 && game.lives < 5) {
              game.lives++;
              setLives(game.lives);
            }

            break;
          }
        }
      }

      if (game.ship && !game.ship.invulnerable) {
        for (const asteroid of game.asteroids) {
          if (checkCollision(game.ship, asteroid)) {
            game.particles.push(...createExplosion(game.ship.x, game.ship.y, 30));
            game.lives--;
            setLives(game.lives);

            if (game.lives <= 0) {
              if (game.score > highScore) {
                localStorage.setItem("asteroids-highscore", game.score.toString());
                setHighScore(game.score);
              }
              setGameState("gameover");
              return;
            }

            game.ship.x = game.canvasWidth / 2;
            game.ship.y = game.canvasHeight / 2;
            game.ship.vx = 0;
            game.ship.vy = 0;
            game.ship.rotation = -Math.PI / 2;
            game.ship.invulnerable = true;
            game.ship.invulnerableTime = INVULNERABLE_TIME;
            break;
          }
        }
      }

      if (game.asteroids.length === 0) {
        game.wave++;
        spawnWave();
      }

      ctx.fillStyle = COLORS.background;
      ctx.fillRect(0, 0, game.canvasWidth, game.canvasHeight);

      ctx.fillStyle = "#ffffff";
      for (let i = 0; i < 100; i++) {
        const x = (i * 137.5) % game.canvasWidth;
        const y = (i * 73.3) % game.canvasHeight;
        const brightness = 0.3 + Math.random() * 0.3;
        ctx.globalAlpha = brightness;
        ctx.fillRect(x, y, 1, 1);
      }
      ctx.globalAlpha = 1;

      for (const particle of game.particles) {
        const alpha = particle.life / particle.maxLife;
        ctx.fillStyle = `rgba(255, 43, 214, ${alpha})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const asteroid of game.asteroids) {
        ctx.save();
        ctx.translate(asteroid.x, asteroid.y);
        ctx.rotate(asteroid.rotation);
        ctx.shadowColor = COLORS.asteroid;
        ctx.shadowBlur = 15;
        ctx.strokeStyle = COLORS.asteroid;
        ctx.lineWidth = 2;

        ctx.beginPath();
        if (asteroid.vertices.length > 0) {
          ctx.moveTo(asteroid.vertices[0].x, asteroid.vertices[0].y);
          for (let i = 1; i < asteroid.vertices.length; i++) {
            ctx.lineTo(asteroid.vertices[i].x, asteroid.vertices[i].y);
          }
        }
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }

      for (const bullet of game.bullets) {
        ctx.save();
        ctx.shadowColor = COLORS.bullet;
        ctx.shadowBlur = 10;
        ctx.fillStyle = COLORS.bullet;
        ctx.beginPath();
        ctx.arc(bullet.x, bullet.y, bullet.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (game.ship) {
        ctx.save();
        ctx.translate(game.ship.x, game.ship.y);
        ctx.rotate(game.ship.rotation);

        if (game.ship.thrusting) {
          ctx.shadowColor = COLORS.thrust;
          ctx.shadowBlur = 15;
          ctx.fillStyle = COLORS.thrust;
          ctx.beginPath();
          ctx.moveTo(-game.ship.radius * 0.6, -game.ship.radius * 0.4);
          ctx.lineTo(-game.ship.radius * 1.5 - Math.random() * 5, 0);
          ctx.lineTo(-game.ship.radius * 0.6, game.ship.radius * 0.4);
          ctx.closePath();
          ctx.fill();
        }

        if (game.ship.invulnerable && Math.floor(game.frameCount / 5) % 2 === 0) {
          ctx.globalAlpha = 0.3;
        }

        ctx.shadowColor = COLORS.ship;
        ctx.shadowBlur = 15;
        ctx.strokeStyle = COLORS.ship;
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(game.ship.radius, 0);
        ctx.lineTo(-game.ship.radius * 0.8, -game.ship.radius * 0.7);
        ctx.lineTo(-game.ship.radius * 0.5, 0);
        ctx.lineTo(-game.ship.radius * 0.8, game.ship.radius * 0.7);
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }

      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationId);
  }, [gameState, spawnWave, canvasSize, highScore]);

  // Starts joystick tracking and starts the game from overlays on first touch.
  const handleJoystickStart = useCallback((e) => {
    if (gameState === "start" || gameState === "gameover") {
      initGame();
      return;
    }
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const startX = touch.clientX - rect.left;
    const startY = touch.clientY - rect.top;
    touchRef.current = {
      active: true,
      startX,
      startY,
      currentX: startX,
      currentY: startY,
    };
    setJoystickActive(true);
    setJoystickOffset({ x: 0, y: 0 });
  }, [gameState, initGame]);

  // Updates the virtual joystick offset while dragging on mobile.
  const handleJoystickMove = useCallback((e) => {
    if (!touchRef.current.active) return;
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = touch.clientX - rect.left;
    const currentY = touch.clientY - rect.top;
    touchRef.current.currentX = currentX;
    touchRef.current.currentY = currentY;
    setJoystickOffset({
      x: (currentX - touchRef.current.startX) * 0.5,
      y: (currentY - touchRef.current.startY) * 0.5,
    });
  }, []);

  // Resets joystick state when touch ends.
  const handleJoystickEnd = useCallback(() => {
    touchRef.current.active = false;
    setJoystickActive(false);
    setJoystickOffset({ x: 0, y: 0 });
  }, []);

  // Starts continuous firing while the fire button is pressed.
  const handleFireStart = useCallback((e) => {
    e.preventDefault();
    if (gameState === "start" || gameState === "gameover") {
      initGame();
      return;
    }
    fireButtonRef.current.firing = true;
  }, [gameState, initGame]);

  // Stops continuous firing when the fire button is released.
  const handleFireEnd = useCallback(() => {
    fireButtonRef.current.firing = false;
  }, []);

  const fontSize = isMobile ? 10 : 16;
  const livesIconSize = isMobile ? 14 : 20;

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        backgroundColor: COLORS.background,
        fontFamily: "var(--font-press-start), monospace",
        touchAction: "none",
        userSelect: "none",
        overflow: "hidden",
        padding: 10,
        boxSizing: "border-box",
      }}
    >
      <div
        onTouchStart={() => {
          if (gameState === "start" || gameState === "gameover") {
            initGame();
          }
        }}
        style={{
          position: "relative",
          border: `2px solid ${COLORS.ship}`,
          boxShadow: `0 0 30px ${COLORS.ship}40`,
          touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          style={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "calc(100vh - 120px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: isMobile ? 10 : 20,
            left: isMobile ? 10 : 20,
            color: COLORS.text,
            fontSize,
            textShadow: `0 0 10px ${COLORS.ship}`,
          }}
        >
          <div>SCORE: {score.toString().padStart(6, "0")}</div>
          <div style={{ fontSize: fontSize * 0.75, marginTop: 8, opacity: 0.7 }}>
            HI: {highScore.toString().padStart(6, "0")}
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            top: isMobile ? 10 : 20,
            right: isMobile ? 10 : 20,
            color: COLORS.text,
            display: "flex",
            gap: isMobile ? 4 : 8,
          }}
        >
          {Array.from({ length: Math.max(0, lives) }).map((_, i) => (
            <svg
              key={i}
              width={livesIconSize}
              height={livesIconSize}
              viewBox="-20 -10 40 20"
              style={{ filter: `drop-shadow(0 0 5px ${COLORS.ship})` }}
            >
              <path
                d="M 20 0 L -16 -14 L -10 0 L -16 14 Z"
                fill="none"
                stroke={COLORS.ship}
                strokeWidth={2}
              />
            </svg>
          ))}
        </div>
        {gameState === "start" && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: COLORS.text,
              textAlign: "center",
              padding: 20,
            }}
          >
            <div
              style={{
                fontSize: isMobile ? 20 : 32,
                textShadow: `0 0 20px ${COLORS.ship}`,
                marginBottom: isMobile ? 20 : 40,
              }}
            >
              ASTROSHOOT
            </div>
            <div style={{ fontSize: isMobile ? 10 : 14, animation: "blink 1s infinite" }}>
              {isMobile ? "TAP TO START" : "PRESS SPACE TO START"}
            </div>
            <div
              style={{
                fontSize: isMobile ? 8 : 10,
                marginTop: isMobile ? 20 : 40,
                opacity: 0.6,
                lineHeight: 2,
              }}
            >
              {isMobile ? (
                <>
                  LEFT SIDE - TOUCH TO MOVE
                  <br />
                  RIGHT SIDE - TAP TO FIRE
                </>
              ) : (
                <>
                  ARROWS / WASD - MOVE
                  <br />
                  SPACE - FIRE
                </>
              )}
            </div>
          </div>
        )}
        {gameState === "gameover" && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: COLORS.text,
              textAlign: "center",
              padding: 20,
            }}
          >
            <div
              style={{
                fontSize: isMobile ? 18 : 28,
                color: COLORS.asteroid,
                textShadow: `0 0 20px ${COLORS.asteroid}`,
                marginBottom: 20,
              }}
            >
              GAME OVER
            </div>
            <div style={{ fontSize: isMobile ? 12 : 16, marginBottom: isMobile ? 20 : 40 }}>
              FINAL SCORE: {score}
            </div>
            <div style={{ fontSize: isMobile ? 10 : 14, animation: "blink 1s infinite" }}>
              {isMobile ? "TAP TO RESTART" : "PRESS SPACE TO RESTART"}
            </div>
          </div>
        )}
      </div>

      {isMobile && gameState === "playing" && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: "35vh",
            display: "flex",
            pointerEvents: "none",
          }}
        >
          <div
            onTouchStart={handleJoystickStart}
            onTouchMove={handleJoystickMove}
            onTouchEnd={handleJoystickEnd}
            style={{
              flex: 1,
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                border: `3px solid ${COLORS.ship}60`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: `${COLORS.ship}10`,
              }}
            >
              {joystickActive && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: COLORS.ship,
                    boxShadow: `0 0 20px ${COLORS.ship}`,
                    position: "absolute",
                    transform: `translate(${joystickOffset.x}px, ${joystickOffset.y}px)`,
                  }}
                />
              )}
              <span style={{ color: COLORS.ship, fontSize: 10, opacity: 0.5 }}>MOVE</span>
            </div>
          </div>
          <div
            onTouchStart={handleFireStart}
            onTouchEnd={handleFireEnd}
            style={{
              flex: 1,
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                border: `3px solid ${COLORS.bullet}60`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: `${COLORS.bullet}10`,
              }}
            >
              <span style={{ color: COLORS.bullet, fontSize: 10, opacity: 0.5 }}>FIRE</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
